import { createContext, useContext, ReactNode } from "react";
import {
  SubscriptionTier,
  SubscriptionStatus,
  Feature,
  TIER_FEATURES,
  TIER_LIMITS,
  TIER_DETAILS,
} from "@/lib/tier-config";

// IQ Assistant standalone — no Stripe / firm_subscriptions yet. Everything
// resolves to the top tier so feature gates pass; billing wires in later.

interface SubscriptionState {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  isTrialing: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  maxAdvisers: number;
  maxClients: number;
  isLoading: boolean;
  marketingPlan: string | null;
  marketingSubscriptionActive: boolean;
  apiAddonActive: boolean;
  featureOverrides: Record<string, boolean>;
}

interface SubscriptionContextType extends SubscriptionState {
  hasFeature: (feature: Feature) => boolean;
  canAddAdviser: (currentCount: number) => boolean;
  canAddClient: (currentCount: number) => boolean;
  refreshSubscription: () => Promise<void>;
  tierDetails: typeof TIER_DETAILS;
}

const TIER: SubscriptionTier = "enterprise" in TIER_LIMITS ? ("enterprise" as SubscriptionTier) : ("essentials" as SubscriptionTier);
const LIMITS = TIER_LIMITS[TIER];

const STATE: SubscriptionState = {
  tier: TIER,
  status: "active" as SubscriptionStatus,
  isTrialing: false,
  trialEndsAt: null,
  currentPeriodEnd: null,
  maxAdvisers: LIMITS.max_advisers,
  maxClients: LIMITS.max_clients,
  isLoading: false,
  marketingPlan: null,
  marketingSubscriptionActive: false,
  apiAddonActive: false,
  featureOverrides: {},
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const hasFeature = (feature: Feature) =>
    TIER_FEATURES[STATE.tier]?.includes(feature) ?? true;
  const canAddAdviser = (n: number) => n < STATE.maxAdvisers;
  const canAddClient  = (n: number) => n < STATE.maxClients;
  const refreshSubscription = async () => { /* noop */ };

  return (
    <SubscriptionContext.Provider value={{
      ...STATE, hasFeature, canAddAdviser, canAddClient, refreshSubscription,
      tierDetails: TIER_DETAILS,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}
